import { defineRouting } from "next-intl/routing";
import { createNavigation } from "next-intl/navigation";
import { generatePublicPaths } from "@/lib/utils";
import { Locale } from "@/models/Locale";

export const localeConfig = {
  locales: ["en", "tr", "ar", "ru", "de", "fr", "es", "zh"] as const,
  defaultLocale: "en" as const,
  localesWLongName: [
    "English",
    "Turkish",
    "Arabic",
    "Russian",
    "German",
    "French",
    "Spanish",
    "Chinese",
  ],
  pathnames: {
    "/contact": {
      en: "/contact",
      tr: "/iletisim",
      ar: "/ittisal",
      ru: "/kontakt",
      de: "/kontakt",
      fr: "/contact",
      es: "/contacto",
      zh: "/lianxi",
    },
    "/sign-up": {
      en: "/sign-up",
      tr: "/kayit-ol",
      ar: "/tasjil",
      ru: "/registratsiya",
      de: "/registrieren",
      fr: "/inscription",
      es: "/registro",
      zh: "/zhuce",
    },
    "/sign-in": {
      en: "/sign-in",
      tr: "/giris-yap",
      ar: "/dukul",
      ru: "/vkhod",
      de: "/anmelden",
      fr: "/connexion",
      es: "/iniciar-sesion",
      zh: "/denglu",
    },
    "/reset-password": {
      en: "/reset-password",
      tr: "/sifre-sifirla",
      ar: "/iadat-kalimat-sir",
      ru: "/sbros-parolya",
      de: "/passwort-zuruecksetzen",
      fr: "/reinitialiser-mot-de-passe",
      es: "/restablecer-contrasena",
      zh: "/chongzhi-mima",
    },
    "/privacy": {
      en: "/privacy",
      tr: "/gizlilik",
      ar: "/khususia",
      ru: "/konfidentsialnost",
      de: "/datenschutz",
      fr: "/confidentialite",
      es: "/privacidad",
      zh: "/yinsi",
    },
    "/gdpr": {
      en: "/gdpr",
      tr: "/gdpr",
      ar: "/himayat-bayanat",
      ru: "/gdpr",
      de: "/dsgvo",
      fr: "/rgpd",
      es: "/rgpd",
      zh: "/gdpr",
    },
    "/forgot-password": {
      en: "/forgot-password",
      tr: "/sifremi-unuttum",
      ar: "/nasit-kalimat-sir",
      ru: "/zabyl-parol",
      de: "/passwort-vergessen",
      fr: "/mot-de-passe-oublie",
      es: "/olvide-contrasena",
      zh: "/wangji-mima",
    },
    "/verify-email": {
      en: "/verify-email",
      tr: "/email-dogrula",
      ar: "/tathiq-bareed",
      ru: "/podtverdit-email",
      de: "/email-bestaetigen",
      fr: "/verifier-email",
      es: "/verificar-email",
      zh: "/yanzheng-youxiang",
    },
    "/verify-pending": {
      en: "/verify-pending",
      tr: "/dogrulama-bekliyor",
      ar: "/tathiq-muntadir",
      ru: "/ozhidanie-podtverzhdeniya",
      de: "/bestaetigung-ausstehend",
      fr: "/verification-en-attente",
      es: "/verificacion-pendiente",
      zh: "/dengdai-yanzheng",
    },
    "/kvkk": {
      en: "/kvkk",
      tr: "/kvkk",
      ar: "/kvkk",
      ru: "/kvkk",
      de: "/kvkk",
      fr: "/kvkk",
      es: "/kvkk",
      zh: "/kvkk",
    },
  },
};

export function isValidLocale(locale: Locale): locale is Locale {
  return (routing.locales as readonly string[]).includes(locale);
}

export const languageNames = {
  en: "English",
  tr: "Turkish",
  ar: "Arabic",
  ru: "Russian",
  de: "German",
  fr: "French",
  es: "Spanish",
  zh: "Chinese",
};

export const routing = defineRouting(localeConfig);

// Lightweight wrappers around Next.js' navigation APIs
// that will consider the routing configuration
export const { Link, redirect, usePathname, useRouter, getPathname } =
  createNavigation(routing);

export const apiAuthPrefix = "/api/auth";

// --- DYNAMICALLY GENERATED PUBLIC PAGES LIST ---
const generatedPaths = generatePublicPaths(localeConfig.pathnames);

export const publicPagesWithLocale = [
  "/", // Manually add the root path
  ...generatedPaths, // Add all paths from your config
  // You can still add other custom paths or regex patterns here
  "/menu/.*",
  "/category/.*",
  "/kvkk", // e.g., for paths not in the main config
];

// OPTIMIZED: Simplified authBypassPaths since matcher now handles most of these
// These are now only for additional runtime checks if needed
export const authBypassPaths = [
  // Keep only the dynamic patterns that can't be handled by matcher
  /^\/menu\/.+/, // Dynamic menu routes
  /^\/category\/.+/, // Dynamic category routes
  /^\/[a-z]{2}\/menu\/.+/, // Localized dynamic menu routes
  /^\/[a-z]{2}\/category\/.+/, // Localized dynamic category routes
  /^\/kvkk$/, // KVKK page (if needed for specific handling)
];

type LocalizedPathKey = keyof typeof localeConfig.pathnames;

const noStoreRouteKeys: (LocalizedPathKey | "/dashboard")[] = [
  "/dashboard",
  "/sign-in",
  "/sign-up",
  "/reset-password",
  "/forgot-password",
  "/verify-email",
  "/verify-pending",
];

export const dynamicNoStorePaths = noStoreRouteKeys.flatMap((key) => {
  return key === "/dashboard"
    ? [key]
    : Object.values(localeConfig.pathnames[key]);
});
